import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Card, CardContent, Chip, Grid, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, IconButton, Menu, ListItemIcon, 
  ListItemText, CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckIcon from '@mui/icons-material/Check';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import TimerIcon from '@mui/icons-material/Timer';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import TradeExecutionService from '../services/TradeExecutionService';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'medium',
  backgroundColor: theme.palette.background.paper,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function Execution() {
  // Data states
  const [activeStrategies, setActiveStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState('all');
  const [executions, setExecutions] = useState([]);
  const [strategyStatus, setStrategyStatus] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedExecution, setSelectedExecution] = useState(null);

  // Load initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      
      try {
        // Get active strategies
        const strategies = await TradeExecutionService.getActiveStrategies();
        setActiveStrategies(strategies);
        
        // Get executions for all or selected strategy
        await fetchExecutions();
      } catch (err) {
        console.error('Error loading trade execution data:', err);
        setError('Failed to load execution data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Setup auto-refresh
  useEffect(() => {
    // Clear any existing timers first
    let timer = null;
    
    if (refreshInterval > 0) {
      // Add some jitter to prevent all clients refreshing at the same time
      const jitter = Math.floor(Math.random() * 500); // Random delay between 0-500ms
      
      timer = setInterval(() => {
        // Only fetch if not already loading
        if (!loading) {
          fetchExecutions();
        }
      }, refreshInterval + jitter);
      
      // Run an immediate fetch when refresh is enabled
      fetchExecutions();
    }
    
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshInterval, loading]);
  
  // Add auto-refresh after a successful operation
  const refreshAfterOperation = () => {
    // Short delay to allow backend to process the change
    setTimeout(() => {
      fetchExecutions();
    }, 500);
  };
  
  // Fetch strategy status when selection changes
  useEffect(() => {
    const fetchStrategyStatus = async () => {
      if (selectedStrategyId === 'all' || !selectedStrategyId) {
        setStrategyStatus(null);
        return;
      }
      
      try {
        const status = await TradeExecutionService.getStrategyExecutionStatus(selectedStrategyId);
        setStrategyStatus(status);
      } catch (err) {
        console.error('Error fetching strategy status:', err);
      }
    };
    
    fetchStrategyStatus();
  }, [selectedStrategyId]);
  
  // Fetch executions based on selected strategy
  const fetchExecutions = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    
    try {
      const strategyId = selectedStrategyId === 'all' ? null : selectedStrategyId;
      const data = await TradeExecutionService.getLiveExecutions(strategyId);
      setExecutions(data.executions);
      setError(null);
    } catch (err) {
      console.error('Error fetching executions:', err);
      setError('Failed to fetch execution data. Please try again.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };
  
  // Handle strategy selection change
  const handleStrategyChange = (event) => {
    setSelectedStrategyId(event.target.value);
  };
  
  // Handle manual refresh
  const handleRefresh = () => {
    fetchExecutions();
  };
  
  // State for execution details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  // State for cancel confirmation dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  // State for success notification
  const [successMessage, setSuccessMessage] = useState('');
  
  // Handle execution actions (cancel, etc.)
  const handleActionClick = (event, execution) => {
    // Prevent the event from being garbage collected before the menu opens
    event.persist && event.persist();
    setSelectedExecution(execution);
    // Use a short timeout to ensure the element is still in the DOM when the menu opens
    setTimeout(() => {
      setActionMenuAnchor(event.currentTarget);
    }, 0);
  };
  
  const handleActionClose = () => {
    setActionMenuAnchor(null);
  };
  
  // Handle showing execution details
  const handleViewDetails = () => {
    handleActionClose();
    setDetailsDialogOpen(true);
  };
  
  // Close details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
  };
  
  // Show cancel confirmation dialog
  const handleCancelConfirmation = () => {
    handleActionClose();
    setCancelDialogOpen(true);
  };
  
  // Close cancel confirmation dialog
  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
  };
  
  // Handle actual execution cancellation
  const handleCancelExecution = async () => {
    if (!selectedExecution) return;
    
    try {
      await TradeExecutionService.cancelExecution(selectedExecution.id);
      
      // Update the execution in the local state immediately without waiting for a refetch
      // This ensures the UI updates immediately
      setExecutions(currentExecutions => 
        currentExecutions.map(exec => 
          exec.id === selectedExecution.id 
            ? { ...exec, status: 'cancelled' } 
            : exec
        )
      );
      
      // Show success message
      setSuccessMessage(`Execution ${selectedExecution.id} successfully cancelled`);
      setTimeout(() => setSuccessMessage(''), 5000); // Clear after 5 seconds
      
      // Use refreshAfterOperation to ensure we get fresh data after a short delay
      refreshAfterOperation();
      
      // Close the dialog
      handleCloseCancelDialog();
    } catch (err) {
      console.error('Failed to cancel execution:', err);
      setError(`Failed to cancel execution: ${err.message}`);
      setTimeout(() => setError(''), 5000); // Clear error after 5 seconds
      handleCloseCancelDialog();
    }
  };
  
  // Render status chip with appropriate color
  const renderStatusChip = (status) => {
    let color, icon;
    
    switch (status.toLowerCase()) {
      case 'completed':
        color = 'success';
        icon = <CheckIcon fontSize="small" />;
        break;
      case 'pending':
        color = 'warning';
        icon = <PendingIcon fontSize="small" />;
        break;
      case 'failed':
        color = 'error';
        icon = <ErrorIcon fontSize="small" />;
        break;
      case 'cancelled':
        color = 'default';
        icon = <CancelIcon fontSize="small" />;
        break;
      default:
        color = 'default';
        icon = null;
    }
    
    return (
      <Chip 
        size="small" 
        color={color} 
        icon={icon}
        label={status.charAt(0).toUpperCase() + status.slice(1)} 
      />
    );
  };
  
  // Format currency values
  const formatCurrency = (value) => {
    if (!value) return '-';
    
    const numValue = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  };

  // Format timestamp to readable time
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header and controls */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
          mb: 2, p: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SwapVertIcon sx={{ fontSize: 28, mr: 1, color: '#00FF94' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
              Trade Execution
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="strategy-select-label">Strategy</InputLabel>
              <Select
                labelId="strategy-select-label"
                value={selectedStrategyId}
                label="Strategy"
                onChange={handleStrategyChange}
              >
                <MenuItem value="all">All Strategies</MenuItem>
                {activeStrategies.map(strategy => (
                  <MenuItem key={strategy.id} value={strategy.id}>
                    {typeof strategy.name === 'string' ? strategy.name : strategy.id} 
                    ({typeof strategy.market === 'string' ? strategy.market : 'Unknown'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
      
      {/* Strategy status card (shown only when a specific strategy is selected) */}
      {strategyStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Strategy Execution Status
              </Typography>
              <Chip 
                color={strategyStatus.status === 'active' ? 'success' : 'default'}
                icon={strategyStatus.status === 'active' ? <PlayArrowIcon /> : <PauseIcon />}
                label={strategyStatus.status.toUpperCase()}
              />
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Executions Today
                </Typography>
                <Typography variant="h6">
                  {strategyStatus.total_executions_today}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Success Rate
                </Typography>
                <Typography variant="h6">
                  {strategyStatus.success_rate}%
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Avg. Execution Time
                </Typography>
                <Typography variant="h6">
                  {strategyStatus.avg_execution_time_ms} ms
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Last Execution
                </Typography>
                <Typography variant="h6">
                  {formatTime(strategyStatus.last_execution)}
                </Typography>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Resource Usage
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Chip size="small" label={`CPU: ${strategyStatus.resources?.cpu_usage || 'N/A'}`} />
                  <Chip size="small" label={`Memory: ${strategyStatus.resources?.memory_usage || 'N/A'}`} />
                </Box>
              </Box>
              
              <Button 
                variant="contained" 
                color={strategyStatus.status === 'active' ? 'error' : 'success'}
                startIcon={strategyStatus.status === 'active' ? <PauseIcon /> : <PlayArrowIcon />}
              >
                {strategyStatus.status === 'active' ? 'Pause Strategy' : 'Activate Strategy'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Main content */}
      {loading && !executions.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading execution data...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : executions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Execution Data Available
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {selectedStrategyId === 'all' 
              ? 'There are no active executions at this time.' 
              : 'This strategy has no executions to display.'}
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Time</StyledTableCell>
                  {selectedStrategyId === 'all' && <StyledTableCell>Strategy</StyledTableCell>}
                  <StyledTableCell>Symbol</StyledTableCell>
                  <StyledTableCell>Action</StyledTableCell>
                  <StyledTableCell align="right">Price</StyledTableCell>
                  <StyledTableCell align="right">Size</StyledTableCell>
                  <StyledTableCell align="right">Value</StyledTableCell>
                  <StyledTableCell>Type</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Route</StyledTableCell>
                  <StyledTableCell align="right">Latency</StyledTableCell>
                  <StyledTableCell align="center">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executions.map((execution) => (
                  <StyledTableRow 
                    key={execution.id} 
                    hover
                    sx={{
                      backgroundColor: execution.status === 'failed' 
                        ? 'error.light' 
                        : undefined
                    }}
                  >
                    <TableCell>
                      {formatTime(execution.timestamp)}
                    </TableCell>
                    {selectedStrategyId === 'all' && (
                      <TableCell>
                        <Tooltip title={execution.market}>
                          <Chip 
                            size="small" 
                            label={execution.strategy_name} 
                            color={
                              execution.market === 'Crypto' ? 'secondary' :
                              execution.market === 'Stocks' ? 'primary' :
                              'default'
                            }
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell>{execution.symbol}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={execution.action.toUpperCase()}
                        color={execution.action === 'buy' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">{execution.price}</TableCell>
                    <TableCell align="right">{execution.size}</TableCell>
                    <TableCell align="right">{formatCurrency(execution.value)}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={execution.execution_type.toUpperCase()}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{renderStatusChip(execution.status)}</TableCell>
                    <TableCell>{execution.route || '-'}</TableCell>
                    <TableCell align="right">
                      {execution.latency_ms ? `${execution.latency_ms} ms` : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small"
                        onClick={(e) => handleActionClick(e, execution)}
                        disabled={execution.status !== 'pending'}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Action menu */}
          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={handleActionClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleCancelConfirmation}>
              <ListItemIcon>
                <CancelIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cancel Execution</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleViewDetails}>
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View Details</ListItemText>
            </MenuItem>
          </Menu>
        </Paper>
      )}
      
      {/* Success message alert */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mt: 2, mb: 2 }}
          onClose={() => setSuccessMessage('')}
        >
          {successMessage}
        </Alert>
      )}
      
      {/* Auto-refresh indicator at bottom */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <TimerIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
          Auto-refreshing every {refreshInterval / 1000} seconds
        </Typography>
        
        <Select
          size="small"
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(Number(e.target.value))}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value={0}>Disabled</MenuItem>
          <MenuItem value={2000}>2 seconds</MenuItem>
          <MenuItem value={5000}>5 seconds</MenuItem>
          <MenuItem value={10000}>10 seconds</MenuItem>
          <MenuItem value={30000}>30 seconds</MenuItem>
        </Select>
      </Box>
      
      {/* Execution Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Execution Details
            <IconButton onClick={handleCloseDetailsDialog} edge="end" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedExecution && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Execution ID</Typography>
                <Typography variant="body1" gutterBottom>{selectedExecution.id}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Strategy</Typography>
                <Typography variant="body1" gutterBottom>{selectedExecution.strategy_name}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Symbol</Typography>
                <Typography variant="body1" gutterBottom>{selectedExecution.symbol}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                <Chip 
                  size="small" 
                  label={selectedExecution.action.toUpperCase()}
                  color={selectedExecution.action === 'buy' ? 'success' : 'error'}
                  sx={{ my: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Price</Typography>
                <Typography variant="body1" gutterBottom>{selectedExecution.price}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Size</Typography>
                <Typography variant="body1" gutterBottom>{selectedExecution.size}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Value</Typography>
                <Typography variant="body1" gutterBottom>{formatCurrency(selectedExecution.value)}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 1, mb: 1 }}>
                  {renderStatusChip(selectedExecution.status)}
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                
                <Typography variant="subtitle2" color="text.secondary">Execution Type</Typography>
                <Typography variant="body1" gutterBottom>{selectedExecution.execution_type}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Route</Typography>
                <Typography variant="body1" gutterBottom>{selectedExecution.route || '-'}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Timestamp</Typography>
                <Typography variant="body1" gutterBottom>{formatTime(selectedExecution.timestamp)}</Typography>
                
                <Typography variant="subtitle2" color="text.secondary">Latency</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedExecution.latency_ms ? `${selectedExecution.latency_ms} ms` : '-'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
          {selectedExecution && typeof selectedExecution === 'object' && selectedExecution.status === 'pending' && (
            <Button 
              color="error" 
              variant="contained" 
              onClick={() => {
                handleCloseDetailsDialog();
                setCancelDialogOpen(true);
              }}
            >
              Cancel Execution
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Cancel Execution Confirmation Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Cancel Execution
        </DialogTitle>
        <DialogContent>
          {selectedExecution && (
            <Typography variant="body1">
              Are you sure you want to cancel the {selectedExecution.action.toUpperCase()} execution of {selectedExecution.size} {selectedExecution.symbol} at {selectedExecution.price}?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>No, Keep Execution</Button>
          <Button 
            color="error" 
            variant="contained" 
            onClick={handleCancelExecution}
          >
            Yes, Cancel Execution
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Execution;
